import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEye, FiShoppingCart, FiCheck,
  FiAlertCircle, FiSave,
  FiZap, FiPlay, FiX, FiGlobe, FiTag,
  FiRefreshCw, FiChevronDown, FiChevronUp,
  FiPower,
} from "react-icons/fi";
import { FaFacebookF } from "react-icons/fa";
import FloatingAdminMenu from "./FloatMenu";

// ─── API BASE ────────────────────────────────────────────────────────────────
const API_BASE = "https://www.medicinacondante.com/cursos.php";

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;1,300&display=swap');
    :root {
      --gold:      #C9A84C;
      --gold-dim:  rgba(201,168,76,0.55);
      --gold-sub:  rgba(201,168,76,0.08);
      --gold-brd:  rgba(201,168,76,0.20);
      --bg:        #0A0906;
      --bg-2:      #0F0E0B;
      --bg-3:      #141208;
      --line:      rgba(201,168,76,0.12);
      --ink:       #EDE0C8;
      --ink-2:     rgba(237,224,200,0.62);
      --ink-3:     rgba(237,224,200,0.32);
      --green:     #6DBF8F;
      --green-dim: rgba(109,191,143,0.18);
      --green-brd: rgba(109,191,143,0.35);
      --red:       #E07A7A;
      --blue:      #6BA3E0;
      --blue-sub:  rgba(107,163,224,0.08);
      --blue-brd:  rgba(107,163,224,0.25);
    }
    *, *::before, *::after { box-sizing: border-box; }
    body { background: var(--bg); font-family: 'Plus Jakarta Sans', sans-serif; }
    ::placeholder { color: var(--ink-3); font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 300; font-style: italic; }
    textarea, input, select { font-family: 'Plus Jakarta Sans', sans-serif; }
    @keyframes adm-spin { to { transform: rotate(360deg); } }
    .adm-spin { animation: adm-spin 0.9s linear infinite; }
    @keyframes pulse-dot {
      0%, 100% { opacity: 0.45; transform: scale(1); }
      50%       { opacity: 1;   transform: scale(1.4); }
    }
    .pulse-anim { animation: pulse-dot 2s ease-in-out infinite; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg-2); }
    ::-webkit-scrollbar-thumb { background: var(--gold-brd); border-radius: 2px; }
  `}</style>
);

// ─── UI HELPERS ──────────────────────────────────────────────────────────────
const SectionLabel = ({ children, style = {} }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, ...style }}>
    <span style={{ display: "inline-block", width: 20, height: 1, background: "var(--gold)", opacity: 0.7 }} />
    <span style={{ fontWeight: 700, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>
      {children}
    </span>
  </div>
);

const Badge = ({ children, variant = "warn" }) => {
  const map = {
    warn:    { color: "var(--gold)",  borderColor: "var(--gold-brd)",  background: "var(--gold-sub)" },
    ok:      { color: "var(--green)", borderColor: "var(--green-brd)", background: "var(--green-dim)" },
    blue:    { color: "var(--blue)",  borderColor: "var(--blue-brd)",  background: "var(--blue-sub)" },
    neutral: { color: "var(--ink-3)", borderColor: "var(--line)",      background: "transparent" },
    red:     { color: "var(--red)",   borderColor: "rgba(224,122,122,0.3)", background: "rgba(224,122,122,0.07)" },
  };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase",
      padding: "3px 10px", borderRadius: 999, border: "1px solid", ...map[variant],
    }}>
      {children}
    </span>
  );
};

const Hint = ({ children }) => (
  <p style={{
    fontSize: 12, fontWeight: 300, color: "var(--ink-2)", lineHeight: 1.85,
    borderLeft: "2px solid rgba(201,168,76,0.25)", paddingLeft: 14, marginTop: 10,
  }}>
    {children}
  </p>
);

const Divider = () => <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />;

const CardTopLine = ({ color = "rgba(201,168,76,0.25)" }) => (
  <div style={{
    position: "absolute", top: 0, left: 16, right: 16, height: 1,
    background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
  }} />
);

const Spinner = ({ size = 16 }) => (
  <div className="adm-spin" style={{
    width: size, height: size, borderRadius: "50%",
    border: "1.5px solid var(--line)", borderTopColor: "var(--gold)",
    flexShrink: 0,
  }} />
);

// ─── STYLED INPUT ─────────────────────────────────────────────────────────────
const StyledInput = ({ value, onChange, placeholder, type = "text", mono = false, disabled = false }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        disabled={disabled} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          background: disabled ? "rgba(10,9,6,0.5)" : "var(--bg-2)",
          color: disabled ? "var(--ink-3)" : "var(--ink)",
          border: `1px solid ${focused ? "rgba(201,168,76,0.6)" : "var(--line)"}`,
          outline: "none", width: "100%", height: 40, padding: "0 12px",
          fontSize: mono ? 12 : 13,
          fontFamily: mono ? "'Courier New', monospace" : "'Plus Jakarta Sans', sans-serif",
          fontWeight: 300, transition: "border-color 0.25s",
          boxShadow: focused ? "0 0 0 4px rgba(201,168,76,0.04)" : "none",
          borderRadius: 0, letterSpacing: mono ? "0.05em" : "normal",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />
      <AnimatePresence>
        {focused && !disabled && (
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }} exit={{ scaleX: 0, opacity: 0 }}
            style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, var(--gold), transparent)", transformOrigin: "left" }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── TOGGLE ───────────────────────────────────────────────────────────────────
const Toggle = ({ value, onChange, pulse, disabled = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, opacity: disabled ? 0.45 : 1 }}>
    <div
      className={value && pulse ? "pulse-anim" : ""}
      style={{ width: 7, height: 7, borderRadius: "50%", background: value ? "var(--green)" : "var(--ink-3)", opacity: value ? 1 : 0.35, transition: "background 0.3s" }}
    />
    <button
      onClick={disabled ? undefined : onChange}
      style={{
        position: "relative", width: 44, height: 24, cursor: disabled ? "not-allowed" : "pointer",
        background: value ? "rgba(109,191,143,0.18)" : "var(--gold-sub)",
        border: `1px solid ${value ? "var(--green-brd)" : "var(--line)"}`,
        borderRadius: 0, transition: "background 0.3s, border-color 0.3s",
      }}
    >
      <motion.div
        animate={{ x: value ? 22 : 2 }}
        transition={{ type: "spring", stiffness: 500, damping: 32 }}
        style={{ position: "absolute", top: 3, width: 16, height: 16, background: value ? "var(--green)" : "rgba(201,168,76,0.55)" }}
      />
    </button>
  </div>
);

// ─── CHECK ITEM ───────────────────────────────────────────────────────────────
const CheckItem = ({ label, ok }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, fontWeight: ok ? 600 : 300, color: ok ? "var(--green)" : "var(--ink-3)" }}>
    {ok
      ? <FiCheck size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
      : <div style={{ width: 14, height: 14, border: "1px solid var(--line)", borderRadius: "50%", flexShrink: 0 }} />
    }
    {label}
  </div>
);

// ─── PIXEL MODE SELECTOR ──────────────────────────────────────────────────────
const PixelModeSelector = ({ mode, onChange, saving }) => {
  const options = [
    { key: "global",     icon: <FiGlobe size={16} />, title: "Pixel global",    desc: "Un solo Pixel ID para todos los cursos", color: "var(--blue)", borderColor: "var(--blue-brd)", bg: "var(--blue-sub)" },
    { key: "individual", icon: <FiTag size={16} />,   title: "Pixel por curso", desc: "Pixel ID distinto para cada curso",       color: "var(--gold)", borderColor: "var(--gold-brd)", bg: "var(--gold-sub)" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      {options.map((opt) => {
        const active = mode === opt.key;
        return (
          <motion.button
            key={opt.key}
            whileHover={{ opacity: saving ? 1 : 0.9 }} whileTap={saving ? {} : { scale: 0.98 }}
            onClick={() => !saving && onChange(opt.key)}
            style={{ padding: "14px 16px", background: active ? opt.bg : "var(--bg-2)", border: `1px solid ${active ? opt.borderColor : "var(--line)"}`, cursor: saving ? "not-allowed" : "pointer", textAlign: "left", transition: "all 0.25s", position: "relative", opacity: saving && !active ? 0.5 : 1 }}
          >
            {active && <div style={{ position: "absolute", top: 0, left: 8, right: 8, height: 1, background: `linear-gradient(90deg, transparent, ${opt.borderColor}, transparent)` }} />}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", background: active ? opt.bg : "transparent", border: `1px solid ${active ? opt.borderColor : "var(--line)"}`, color: active ? opt.color : "var(--ink-3)", transition: "all 0.25s" }}>{opt.icon}</div>
              <p style={{ fontSize: 12, fontWeight: 700, color: active ? opt.color : "var(--ink-2)", marginBottom: 2 }}>{opt.title}</p>
              {active && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ marginLeft: "auto", width: 18, height: 18, borderRadius: "50%", background: saving ? "var(--ink-3)" : opt.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {saving ? <Spinner size={10} /> : <FiCheck size={10} style={{ color: "#0A0906" }} />}
                </motion.div>
              )}
            </div>
            <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.5 }}>{opt.desc}</p>
          </motion.button>
        );
      })}
    </div>
  );
};

// ─── CURSOS PIXEL TABLE ───────────────────────────────────────────────────────
const CursosPixelTable = ({ cursos, pixelMap, onPixelChange, savingMap, loading }) => {
  const [expanded, setExpanded] = useState(null);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 0", color: "var(--ink-3)" }}>
      <Spinner /><span style={{ fontSize: 12, fontWeight: 300 }}>Cargando cursos…</span>
    </div>
  );

  if (!cursos.length) return (
    <div style={{ padding: "16px 0", fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>No se encontraron cursos en la API.</div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0, border: "1px solid var(--line)", overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 180px 100px 28px", padding: "8px 14px", background: "var(--bg-2)", borderBottom: "1px solid var(--line)" }}>
        {["Curso", "Pixel ID", "Acción", ""].map((h, i) => (
          <span key={i} style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)" }}>{h}</span>
        ))}
      </div>

      {cursos.map((curso, i) => {
        const pid      = pixelMap[curso.id] ?? "";
        const hasPixel = pid.trim().length >= 10;
        const isOpen   = expanded === curso.id;
        const isSaving = savingMap[curso.id];
        // estado_pixel del curso individual
        const estadoPixelCurso = Number(curso.estado_pixel) === 1;

        return (
          <div key={curso.id} style={{ borderBottom: i < cursos.length - 1 ? "1px solid var(--line)" : "none", background: isOpen ? "rgba(201,168,76,0.03)" : "var(--bg-3)", transition: "background 0.2s" }}>
            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 180px 100px 28px", alignItems: "center", padding: "10px 14px", gap: 10, cursor: "pointer" }}
              onClick={() => setExpanded(isOpen ? null : curso.id)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div style={{ width: 28, height: 28, flexShrink: 0, overflow: "hidden", border: "1px solid var(--line)", background: "var(--bg-2)" }}>
                  {curso.imagen
                    ? <img src={curso.imagen} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><FiShoppingCart size={10} style={{ color: "var(--gold-brd)" }} /></div>
                  }
                </div>
                <p style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{curso.nombre_curso || "Sin nombre"}</p>
                {hasPixel && <Badge variant="ok">✓</Badge>}
                {/* Indicador de estado_pixel por curso */}
                <div style={{
                  width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                  background: estadoPixelCurso ? "var(--green)" : "var(--red)",
                  boxShadow: estadoPixelCurso ? "0 0 4px rgba(109,191,143,0.6)" : "0 0 4px rgba(224,122,122,0.5)",
                  title: estadoPixelCurso ? "Pixel activo" : "Pixel inactivo",
                }} title={estadoPixelCurso ? "Pixel activo" : "Pixel inactivo"} />
              </div>

              <div style={{ minWidth: 0 }}>
                {pid
                  ? <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, color: hasPixel ? "var(--gold)" : "var(--ink-3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{pid}</span>
                  : <span style={{ fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>Sin asignar</span>
                }
              </div>

              <div onClick={(e) => e.stopPropagation()}>
                <motion.button
                  whileHover={!isSaving ? { opacity: 0.8 } : {}} whileTap={!isSaving ? { scale: 0.97 } : {}}
                  onClick={() => onPixelChange(curso, pid)} disabled={isSaving}
                  style={{
                    display: "flex", alignItems: "center", gap: 5, justifyContent: "center", width: "100%", height: 30,
                    background: isSaving ? "rgba(109,191,143,0.08)" : hasPixel ? "rgba(109,191,143,0.14)" : "var(--gold-sub)",
                    border: `1px solid ${isSaving ? "var(--green-brd)" : hasPixel ? "var(--green-brd)" : "var(--gold-brd)"}`,
                    color: isSaving ? "var(--green)" : hasPixel ? "var(--green)" : "var(--gold)",
                    cursor: isSaving ? "not-allowed" : "pointer", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                  }}
                >
                  {isSaving ? <Spinner size={10} /> : <FiSave size={10} />}
                  {isSaving ? "Guardando" : "Guardar"}
                </motion.button>
              </div>

              <div style={{ display: "flex", justifyContent: "center", color: "var(--gold-dim)" }}>
                {isOpen ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}
              </div>
            </div>

            <AnimatePresence>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
                  <div style={{ padding: "0 14px 14px", display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <StyledInput value={pid} onChange={(e) => { onPixelChange(curso, e.target.value, true); }} placeholder="Pixel ID para este curso (ej: 1234567890123456)" mono />
                    </div>
                    {pid && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.95 }}
                        onClick={() => onPixelChange(curso, "", true)}
                        style={{ width: 36, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "1px solid rgba(224,122,122,0.25)", color: "var(--red)", cursor: "pointer", flexShrink: 0 }}
                      >
                        <FiX size={12} />
                      </motion.button>
                    )}
                  </div>
                  {/* Info estado_pixel del curso */}
                  <div style={{ padding: "0 14px 14px" }}>
                    <div style={{
                      padding: "8px 12px", display: "flex", alignItems: "center", gap: 8,
                      background: estadoPixelCurso ? "rgba(109,191,143,0.06)" : "rgba(224,122,122,0.06)",
                      border: `1px solid ${estadoPixelCurso ? "rgba(109,191,143,0.2)" : "rgba(224,122,122,0.2)"}`,
                    }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: estadoPixelCurso ? "var(--green)" : "var(--red)", flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 300, color: "var(--ink-2)", fontFamily: "'Courier New', monospace" }}>
                        estado_pixel = {estadoPixelCurso ? "1" : "0"} — {estadoPixelCurso ? "Evento Purchase activo para este curso" : "Evento Purchase desactivado para este curso"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <div style={{ padding: "8px 14px", background: "var(--bg-2)", borderTop: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 300, color: "var(--ink-3)" }}>
          {Object.values(pixelMap).filter((v) => v && v.trim().length >= 10).length} de {cursos.length} cursos con pixel asignado
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          {cursos.map((c) => (
            <div key={c.id} title={c.nombre_curso} style={{
              width: 5, height: 5, borderRadius: "50%",
              background: pixelMap[c.id]?.trim().length >= 10 ? "var(--green)" : "var(--line)",
              transition: "background 0.3s",
            }} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── TABS ────────────────────────────────────────────────────────────────────
const TABS = [
  { key: "eventos", label: "Eventos", icon: <FiZap size={12} /> },
  { key: "probar",  label: "Probar",  icon: <FiPlay size={12} /> },
];

const fadeUp    = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const tabVariant = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } }, exit: { opacity: 0, y: -6, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] } } };

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
const AdminMetaPixel = () => {
  const [activeTab,  setActiveTab]  = useState("eventos");
  const [eventLog,   setEventLog]   = useState([]);
  const [toast,      setToast]      = useState({ show: false, msg: "", type: "ok" });
  const [showMenu,   setShowMenu]   = useState(false);

  // ── Pixel global ──────────────────────────────────────────────────────────
  const [pixelInput,    setPixelInput]    = useState("");
  const [pixelId,       setPixelId]       = useState("");
  const [savingGlobal,  setSavingGlobal]  = useState(false);
  const [pixelMetaDbId, setPixelMetaDbId] = useState(null);

  // ── Estado PageView ───────────────────────────────────────────────────────
  const [pageViewActivo,       setPageViewActivo]       = useState(true);
  const [savingPageViewEstado, setSavingPageViewEstado] = useState(false);

  // ── Eventos Purchase ──────────────────────────────────────────────────────
  const [events, setEvents] = useState({
    pv:  { active: true  },
    pur: { active: false, value: "", currency: "ARS", content: "" },
  });

  // ── Pixel mode ────────────────────────────────────────────────────────────
  const [pixelMode,       setPixelMode]       = useState("global");
  const [savingPixelMode, setSavingPixelMode] = useState(false);

  // ── Cursos ────────────────────────────────────────────────────────────────
  const [cursos,        setCursos]        = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [pixelMap,      setPixelMap]      = useState({});
  const [savingMap,     setSavingMap]     = useState({});

  // ── Guardando bulk estado_pixel ───────────────────────────────────────────
  const [savingBulkEstado, setSavingBulkEstado] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = "ok") => {
    setToast({ show: true, msg, type });
    setTimeout(() => setToast((p) => ({ ...p, show: false })), 2800);
  }, []);

  // ── Cargar pixel_meta al montar ───────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}?recurso=pixel`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data) || !data.length) return;
        const registro = data[0];
        if (registro?.pixel_id) {
          setPixelId(registro.pixel_id);
          setPixelInput(registro.pixel_id);
          setPixelMetaDbId(registro.id);
          setPixelMode(Number(registro.pixel_global) === 1 ? "global" : "individual");
          const estadoActivo = Number(registro.estado) === 1;
          setPageViewActivo(estadoActivo);
          setEvents((prev) => ({ ...prev, pv: { active: estadoActivo } }));
        }
      })
      .catch(() => {});
  }, []);

  // ── Toggle estado PageView ────────────────────────────────────────────────
  const handleTogglePageView = useCallback(async () => {
    if (savingPageViewEstado) return;
    const nuevoEstado = !pageViewActivo;
    if (!pixelMetaDbId) {
      setPageViewActivo(nuevoEstado);
      setEvents((prev) => ({ ...prev, pv: { active: nuevoEstado } }));
      showToast(nuevoEstado ? "PageView activado (guardá el Pixel ID primero)" : "PageView desactivado", "warn");
      return;
    }
    setSavingPageViewEstado(true);
    try {
      const response = await fetch(`${API_BASE}?recurso=pixel&id=${pixelMetaDbId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pixel_id: pixelId, evento: "PageView", pixel_global: pixelMode === "global" ? 1 : 0, estado: nuevoEstado ? 1 : 0 }),
      });
      const result = await response.json();
      if (result.success) {
        setPageViewActivo(nuevoEstado);
        setEvents((prev) => ({ ...prev, pv: { active: nuevoEstado } }));
        showToast(nuevoEstado ? "✓ PageView activado — el pixel se cargará en el <head>" : "PageView desactivado — el pixel NO se cargará en el <head>");
      } else { showToast("Error al cambiar el estado del PageView", "err"); }
    } catch { showToast("Error de red al cambiar el estado", "err"); }
    finally { setSavingPageViewEstado(false); }
  }, [pageViewActivo, pixelMetaDbId, pixelId, pixelMode, savingPageViewEstado, showToast]);

  // ── Toggle Purchase → también actualiza estado_pixel en TODOS los cursos ──
  const handleTogglePurchase = useCallback(async () => {
    const nuevoActivo = !events.pur.active;

    // Actualizar estado local inmediatamente
    setEvents((prev) => ({ ...prev, pur: { ...prev.pur, active: nuevoActivo } }));

    // Si no hay cursos cargados todavía, cargarlos primero
    if (!cursos.length && nuevoActivo) {
      // los cargará el useEffect que observa events.pur.active
      return;
    }

    // Actualizar estado_pixel en TODOS los cursos via bulk endpoint
    setSavingBulkEstado(true);
    try {
      const response = await fetch(`${API_BASE}?recurso=cursos&accion=bulk_estado_pixel`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado_pixel: nuevoActivo ? 1 : 0 }),
      });
      const result = await response.json();
      if (result.success) {
        // Actualizar el estado local de cada curso en el mapa
        setCursos((prev) =>
          prev.map((c) => ({ ...c, estado_pixel: nuevoActivo ? 1 : 0 }))
        );
        showToast(
          nuevoActivo
            ? `Purchase activo`
            : `Purchase desactivado`
        );
      } else {
        showToast("Error al actualizar estado_pixel en cursos", "err");
      }
    } catch { showToast("Error de red al actualizar estado_pixel", "err"); }
    finally { setSavingBulkEstado(false); }
  }, [events.pur.active, cursos.length, showToast]);

  // ── Cambiar modo global/individual ───────────────────────────────────────
  const handlePixelModeChange = useCallback(async (newMode) => {
    if (newMode === pixelMode || savingPixelMode) return;
    if (!pixelMetaDbId) {
      setPixelMode(newMode);
      showToast(newMode === "global" ? "Modo global seleccionado (guardá el Pixel ID primero)" : "Modo individual seleccionado (guardá el Pixel ID primero)", "warn");
      return;
    }
    setSavingPixelMode(true);
    try {
      const response = await fetch(`${API_BASE}?recurso=pixel&id=${pixelMetaDbId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pixel_id: pixelId, evento: "PageView", pixel_global: newMode === "global" ? 1 : 0, estado: pageViewActivo ? 1 : 0 }),
      });
      const result = await response.json();
      if (result.success) {
        setPixelMode(newMode);
        showToast(newMode === "global" ? "Modo global activado — pixel_global = true" : "Modo individual activado — pixel_global = false");
      } else { showToast("Error al cambiar el modo de pixel", "err"); }
    } catch { showToast("Error de red al cambiar el modo", "err"); }
    finally { setSavingPixelMode(false); }
  }, [pixelMode, pixelMetaDbId, pixelId, pageViewActivo, savingPixelMode, showToast]);

  // ── Cargar cursos ─────────────────────────────────────────────────────────
  const fetchCursos = useCallback(() => {
    setCursosLoading(true);
    fetch(`${API_BASE}?recurso=cursos`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setCursos(data);
          setPixelMap((prev) => {
            const next = { ...prev };
            data.forEach((c) => { if (!(c.id in next)) next[c.id] = c.pixel || ""; });
            return next;
          });
        }
      })
      .catch(() => {})
      .finally(() => setCursosLoading(false));
  }, []);

  useEffect(() => {
    if (events.pur.active) fetchCursos();
  }, [events.pur.active, fetchCursos]);

  // ── Guardar Pixel global ──────────────────────────────────────────────────
  const saveGlobalPixel = async () => {
    const val = pixelInput.trim();
    if (!val || !/^\d{10,20}$/.test(val)) { showToast("Ingresá un Pixel ID válido (solo números, 10–20 dígitos)", "err"); return; }
    setSavingGlobal(true);
    try {
      let response;
      if (pixelMetaDbId) {
        response = await fetch(`${API_BASE}?recurso=pixel&id=${pixelMetaDbId}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pixel_id: val, evento: "PageView", pixel_global: pixelMode === "global" ? 1 : 0, estado: pageViewActivo ? 1 : 0 }) });
      } else {
        response = await fetch(`${API_BASE}?recurso=pixel`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pixel_id: val, evento: "PageView", pixel_global: pixelMode === "global" ? 1 : 0, estado: pageViewActivo ? 1 : 0 }) });
      }
      const result = await response.json();
      if (result.success || result.id) { setPixelId(val); if (result.id) setPixelMetaDbId(result.id); showToast("Pixel ID guardado en pixel_meta"); }
      else { showToast("Error al guardar el Pixel ID", "err"); }
    } catch { showToast("Error de red al guardar el Pixel ID", "err"); }
    finally { setSavingGlobal(false); }
  };

  // ── Guardar pixel por curso ───────────────────────────────────────────────
  const handleCursoPixelChange = useCallback(async (curso, value, localOnly = false) => {
    setPixelMap((prev) => ({ ...prev, [curso.id]: value }));
    if (localOnly) return;
    if (value.trim() && !/^\d{10,20}$/.test(value.trim())) { showToast(`Pixel ID inválido para "${curso.nombre_curso}"`, "err"); return; }
    setSavingMap((prev) => ({ ...prev, [curso.id]: true }));
    try {
      const response = await fetch(`${API_BASE}?recurso=cursos&id=${curso.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imagen: curso.imagen || "", pixel: value.trim(), link: curso.link || "", nombre_curso: curso.nombre_curso || "", precio: curso.precio ?? null, descripcion: curso.descripcion || "", estado_pixel: curso.estado_pixel ?? 1 }),
      });
      const result = await response.json();
      if (result.success) { showToast(`Pixel guardado para "${curso.nombre_curso}"`); setCursos((prev) => prev.map((c) => c.id === curso.id ? { ...c, pixel: value.trim() } : c)); }
      else { showToast(`Error al guardar pixel de "${curso.nombre_curso}"`, "err"); }
    } catch { showToast("Error de red al guardar pixel del curso", "err"); }
    finally { setSavingMap((prev) => ({ ...prev, [curso.id]: false })); }
  }, [showToast]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const updatePur = (field, val) => setEvents((prev) => ({ ...prev, pur: { ...prev.pur, [field]: val } }));
  const logEvent  = (name, extra = "") => {
    const now = new Date().toLocaleTimeString("es-AR");
    setEventLog((prev) => [...prev, { time: now, name, extra, id: Date.now() }]);
  };

  // ── Checklist ─────────────────────────────────────────────────────────────
  const cursosConPixelActivo = cursos.filter((c) => Number(c.estado_pixel) === 1).length;
  const individualOk = pixelMode === "individual"
    ? cursos.length > 0 && Object.values(pixelMap).some((v) => v?.trim().length >= 10)
    : true;

  const checks = [
    { label: "Pixel ID global configurado (pixel_meta)",                  ok: !!pixelId },
    { label: "Evento PageView activo (estado = 1 en pixel_meta)",         ok: pageViewActivo },
    { label: "Evento Purchase activo",                                     ok: events.pur.active },
    { label: "Parámetros de Purchase definidos",                           ok: events.pur.active && !!events.pur.value },
    ...(pixelMode === "individual" && events.pur.active
      ? [{ label: "Píxel individual asignado a al menos un curso",        ok: individualOk }]
      : []),
    ...(events.pur.active && cursos.length > 0
      ? [{ label: `estado_pixel = 1 en todos los cursos (${cursosConPixelActivo}/${cursos.length})`, ok: cursosConPixelActivo === cursos.length }]
      : []),
  ];
  const allOk = checks.every((c) => c.ok);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles />
      <div style={{ background: "var(--bg)", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', sans-serif", color: "var(--ink)" }}>
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 1, zIndex: 50, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }} />
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse at 15% 0%, rgba(201,168,76,0.06) 0%, transparent 55%)" }} />

        <FloatingAdminMenu showMenu={showMenu} setShowMenu={setShowMenu} />

        <div style={{ maxWidth: 860, margin: "0 auto", padding: "clamp(64px,8vw,96px) clamp(16px,5vw,48px) 96px", position: "relative", zIndex: 10 }}>

          {/* ══ HEADER ══ */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" style={{ marginBottom: 40 }}>
            <SectionLabel style={{ marginBottom: 12 }}>Integración publicitaria</SectionLabel>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
                  <div style={{ width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, rgba(0,130,251,0.18), rgba(174,0,214,0.18))", border: "1px solid rgba(107,163,224,0.3)" }}>
                    <FaFacebookF size={20} style={{ color: "#0082FB" }} />
                  </div>
                  <h1 style={{ fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.05 }}>
                    Meta <em style={{ color: "var(--gold)", fontStyle: "normal" }}>Ads</em> — Píxel Manager
                  </h1>
                </div>
                <p style={{ fontSize: 13, fontWeight: 300, color: "var(--ink-3)", fontStyle: "italic" }}>
                  Configurá y desplegá tu píxel de seguimiento en la landing de IDM Medicina
                </p>
              </div>
              <Badge variant={allOk ? "ok" : pixelId ? "warn" : "neutral"}>
                {allOk ? "✓ Listo" : pixelId ? "Incompleto" : "Sin configurar"}
              </Badge>
            </div>
            <motion.div style={{ height: 1, background: "linear-gradient(90deg, var(--gold), transparent)", opacity: 0.14, marginTop: 24 }} initial={{ scaleX: 0, originX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }} />
          </motion.div>

          {/* ══ PIXEL ID GLOBAL ══ */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" style={{ marginBottom: 28 }}>
            <SectionLabel style={{ marginBottom: 14 }}>1 · Identificador del píxel global (pixel_meta)</SectionLabel>
            <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "clamp(20px,4vw,32px)", position: "relative" }}>
              <CardTopLine />
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "8px 12px", background: "var(--blue-sub)", border: "1px solid var(--blue-brd)" }}>
                <FiGlobe size={11} style={{ color: "var(--blue)", flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                  Este Pixel .
                </span>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                <div style={{ flex: 1 }}>
                  <StyledInput value={pixelInput} onChange={(e) => setPixelInput(e.target.value)} placeholder="Ej: 1234567890123456" mono disabled={savingGlobal} />
                </div>
                <motion.button
                  whileHover={!savingGlobal ? { opacity: 0.87 } : {}} whileTap={!savingGlobal ? { scale: 0.97 } : {}}
                  onClick={saveGlobalPixel} disabled={savingGlobal}
                  style={{ padding: "0 22px", background: savingGlobal ? "rgba(201,168,76,0.4)" : "var(--gold)", color: "#0A0906", border: "none", cursor: savingGlobal ? "not-allowed" : "pointer", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", height: 40, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                >
                  {savingGlobal ? <Spinner size={12} /> : <FiSave size={13} />}
                  {savingGlobal ? "Guardando…" : pixelMetaDbId ? "Actualizar" : "Guardar"}
                </motion.button>
              </div>
              <AnimatePresence>
                {pixelId && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "var(--gold)", background: "var(--gold-sub)", border: "1px solid var(--gold-brd)", padding: "5px 12px", letterSpacing: "0.08em" }}>{pixelId}</span>
                    <span style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 300, fontStyle: "italic" }}>{pixelMetaDbId ? `Registro #${pixelMetaDbId} en pixel_meta` : "Guardado localmente"}</span>
                    <Badge variant={pageViewActivo ? "ok" : "red"}>{pageViewActivo ? "✓ PageView activo" : "✗ PageView inactivo"}</Badge>
                  </motion.div>
                )}
              </AnimatePresence>
              <Hint>Encontrás el Pixel ID en Meta Business Suite → Administrador de eventos → Píxeles. Es un número de 15–16 dígitos.</Hint>
            </div>
          </motion.div>

          {/* ══ TABS ══ */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--line)", marginBottom: 24 }}>
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: "10px 20px", background: "transparent", border: "none", borderBottom: `2px solid ${activeTab === t.key ? "var(--gold)" : "transparent"}`, color: activeTab === t.key ? "var(--gold)" : "var(--ink-3)", fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "color 0.2s, border-color 0.2s" }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* ══ CONTENIDO POR TAB ══ */}
          <AnimatePresence mode="wait">

          {/* ── TAB EVENTOS ── */}
  {activeTab === "eventos" && (
    <motion.div key="eventos" variants={tabVariant} initial="hidden" animate="show" exit="exit">
      <SectionLabel style={{ marginBottom: 16 }}>2 · Eventos a trackear</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 14 }}>

        {/* ── PageView Card ── */}
        <div style={{
          background: "var(--bg-3)",
          border: `1px solid ${pageViewActivo ? "rgba(107,163,224,0.3)" : "rgba(224,122,122,0.25)"}`,
          padding: "22px 24px", position: "relative", transition: "border-color 0.3s",
        }}>
          <CardTopLine color={pageViewActivo ? "rgba(107,163,224,0.22)" : "rgba(224,122,122,0.18)"} />

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Ícono con estado de color */}
              <div style={{
                width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
                border: `1px solid ${pageViewActivo ? "var(--blue-brd)" : "rgba(224,122,122,0.3)"}`,
                background: pageViewActivo ? "var(--blue-sub)" : "rgba(224,122,122,0.07)",
                color: pageViewActivo ? "var(--blue)" : "var(--red)",
                transition: "all 0.35s",
                position: "relative",
              }}>
                <FiEye size={18} />
                {/* Dot de estado */}
                <div style={{
                  position: "absolute", bottom: 6, right: 6,
                  width: 6, height: 6, borderRadius: "50%",
                  background: pageViewActivo ? "var(--green)" : "var(--red)",
                  boxShadow: pageViewActivo ? "0 0 5px rgba(109,191,143,0.8)" : "0 0 5px rgba(224,122,122,0.6)",
                  transition: "background 0.3s, box-shadow 0.3s",
                }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 3 }}>PageView</p>
                <p style={{ fontSize: 11, color: pageViewActivo ? "var(--blue)" : "var(--ink-3)", fontWeight: 300 }}>
                  {pageViewActivo ? "Activo en todas las páginas" : "Desactivado"}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {savingPageViewEstado && <Spinner size={12} />}
              <Toggle value={pageViewActivo} onChange={handleTogglePageView} pulse={pageViewActivo} disabled={savingPageViewEstado} />
            </div>
          </div>

          <Divider />

          {/* Estado visual — abstracto */}
          <AnimatePresence mode="wait">
            {pageViewActivo ? (
              <motion.div
                key="pv-on"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(107,163,224,0.05)", border: "1px solid rgba(107,163,224,0.15)" }}
              >
                {/* Ícono señal WiFi — representa "transmitiendo" */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M5 12.55a11 11 0 0 1 14.08 0"   stroke="rgba(107,163,224,0.35)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M1.42 9a16 16 0 0 1 21.16 0"    stroke="rgba(107,163,224,0.25)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0"   stroke="#6BA3E0"               strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12" cy="20" r="1.2" fill="#6BA3E0"/>
                </svg>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "var(--blue)", marginBottom: 2 }}>Registrando visitas</p>
                  <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.6 }}>
                    Cada vez que alguien abre el sitio, se registra automáticamente.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="pv-off"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(224,122,122,0.05)", border: "1px solid rgba(224,122,122,0.18)" }}
              >
                {/* Ícono señal WiFi tachada */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M5 12.55a11 11 0 0 1 14.08 0"   stroke="rgba(224,122,122,0.25)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M1.42 9a16 16 0 0 1 21.16 0"    stroke="rgba(224,122,122,0.15)" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0"   stroke="rgba(224,122,122,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12" cy="20" r="1.2" fill="rgba(224,122,122,0.5)"/>
                  <line x1="3" y1="3" x2="21" y2="21" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "var(--red)", marginBottom: 2 }}>Sin registrar visitas</p>
                  <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.6 }}>
                    Las visitas al sitio no se están rastreando en este momento.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Purchase Card ── */}
        <div style={{
          background: "var(--bg-3)",
          border: `1px solid ${events.pur.active ? "rgba(109,191,143,0.3)" : "var(--line)"}`,
          padding: "22px 24px", position: "relative", transition: "border-color 0.3s",
        }}>
          <CardTopLine color={events.pur.active ? "rgba(109,191,143,0.22)" : "rgba(201,168,76,0.12)"} />

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
                border: `1px solid ${events.pur.active ? "var(--green-brd)" : "var(--line)"}`,
                background: events.pur.active ? "var(--green-dim)" : "transparent",
                color: events.pur.active ? "var(--green)" : "var(--ink-3)",
                transition: "all 0.35s", position: "relative",
              }}>
                <FiShoppingCart size={18} />
                <div style={{
                  position: "absolute", bottom: 6, right: 6,
                  width: 6, height: 6, borderRadius: "50%",
                  background: events.pur.active ? "var(--green)" : "var(--ink-3)",
                  boxShadow: events.pur.active ? "0 0 5px rgba(109,191,143,0.8)" : "none",
                  opacity: events.pur.active ? 1 : 0.3,
                  transition: "background 0.3s, box-shadow 0.3s, opacity 0.3s",
                }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 3 }}>Compras</p>
                <p style={{ fontSize: 11, color: events.pur.active ? "var(--green)" : "var(--ink-3)", fontWeight: 300 }}>
                  {events.pur.active ? "Registrando compras" : "Desactivado"}
                </p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {savingBulkEstado && <Spinner size={12} />}
              <Toggle value={events.pur.active} onChange={handleTogglePurchase} pulse={events.pur.active} disabled={savingBulkEstado} />
            </div>
          </div>

          <Divider />

          {/* Estado visual — abstracto */}
          <AnimatePresence mode="wait">
            {events.pur.active ? (
              <motion.div
                key="pur-on"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(109,191,143,0.05)", border: "1px solid rgba(109,191,143,0.15)" }}
              >
                {/* Ícono check con círculo — representa "confirmado" */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="rgba(109,191,143,0.35)" strokeWidth="1.5"/>
                  <path d="M7.5 12l3 3 6-6" stroke="#6DBF8F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "var(--green)", marginBottom: 2 }}>Registrando compras</p>
                  <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.6 }}>
                    Cada vez que alguien compra un curso, se registra el evento.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="pur-off"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(201,168,76,0.04)", border: "1px solid rgba(201,168,76,0.12)" }}
              >
                {/* Ícono pause — representa "en pausa" */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="rgba(201,168,76,0.3)" strokeWidth="1.5"/>
                  <rect x="8"  y="8" width="2.5" height="8" rx="1" fill="rgba(201,168,76,0.5)"/>
                  <rect x="13.5" y="8" width="2.5" height="8" rx="1" fill="rgba(201,168,76,0.5)"/>
                </svg>
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "var(--gold-dim)", marginBottom: 2 }}>En pausa</p>
                  <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-3)", lineHeight: 1.6 }}>
                    Las compras no se registran en este momento.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Configuración expandida — solo cuando está activo */}
          <AnimatePresence>
            {events.pur.active && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                style={{ overflow: "hidden" }}
              >
                <Divider />
                <SectionLabel style={{ marginBottom: 10 }}>Modo de asignación del píxel</SectionLabel>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10, padding: "8px 12px", background: savingPixelMode ? "rgba(201,168,76,0.05)" : "var(--bg-2)", border: `1px solid ${savingPixelMode ? "var(--gold-brd)" : "var(--line)"}`, transition: "all 0.3s" }}>
                  {savingPixelMode ? <Spinner size={11} /> : <FiGlobe size={11} style={{ color: "var(--ink-3)", flexShrink: 0, marginTop: 1 }} />}
                  <span style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                    {savingPixelMode
                      ? "Guardando modo…"
                      : <>Al cambiar el modo se actualiza <code style={{ fontFamily: "Courier New", color: "var(--gold)", fontSize: 11 }}> pixel global</code> en <code style={{ fontFamily: "Courier New", color: "var(--blue)", fontSize: 11 }}> pixel por curso</code>.</>
                    }
                  </span>
                </div>
                <PixelModeSelector mode={pixelMode} onChange={handlePixelModeChange} saving={savingPixelMode} />
                <div style={{ height: 14 }} />

                <AnimatePresence mode="wait">
                  {pixelMode === "global" && (
                    <motion.div key="global-params" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
                      <SectionLabel style={{ marginBottom: 10 }}>Parámetros del evento</SectionLabel>
                      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Valor</p>
                          <StyledInput value={events.pur.value} onChange={(e) => updatePur("value", e.target.value)} placeholder="29900" type="number" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Moneda</p>
                          <StyledInput value={events.pur.currency} onChange={(e) => updatePur("currency", e.target.value)} placeholder="ARS" />
                        </div>
                      </div>
                      <div>
                        <p style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>Nombre del producto</p>
                        <StyledInput value={events.pur.content} onChange={(e) => updatePur("content", e.target.value)} placeholder="Curso IDM" />
                      </div>
                      <Hint>Se usará el mismo Pixel ID para todos los eventos de compra.</Hint>
                    </motion.div>
                  )}
                  {pixelMode === "individual" && (
                    <motion.div key="individual-params" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "8px 12px", background: "var(--gold-sub)", border: "1px solid var(--gold-brd)" }}>
                        <FiTag size={11} style={{ color: "var(--gold)", flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                          Cada curso usa su propio Pixel ID al registrar la compra.
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                        <SectionLabel>Pixel por curso</SectionLabel>
                        <motion.button whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.95 }} onClick={fetchCursos} style={{ display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "1px solid var(--line)", color: "var(--gold-dim)", padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                          <FiRefreshCw size={10} className={cursosLoading ? "adm-spin" : ""} /> Actualizar
                        </motion.button>
                      </div>
                      <CursosPixelTable cursos={cursos} pixelMap={pixelMap} onPixelChange={handleCursoPixelChange} savingMap={savingMap} loading={cursosLoading} />
                      <Hint>Expandí un curso para editar su Pixel ID y luego guardá.</Hint>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </motion.div>
  )}   
            {/* ── TAB PROBAR ── */}
            {activeTab === "probar" && (
              <motion.div key="probar" variants={tabVariant} initial="hidden" animate="show" exit="exit">
                <SectionLabel style={{ marginBottom: 20 }}>3 · Verificar integración</SectionLabel>

                <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "22px 24px", marginBottom: 16, position: "relative" }}>
                  <CardTopLine />
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Simulación de eventos</p>
                  <Hint>Simulá el disparo de cada evento. En producción verificá con Meta Pixel Helper (extensión Chrome).</Hint>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "18px 0" }}>
                    <motion.button whileHover={pageViewActivo ? { background: "rgba(107,163,224,0.15)" } : {}} whileTap={pageViewActivo ? { scale: 0.97 } : {}}
                      onClick={() => pageViewActivo && logEvent("PageView", `estado:1, pixel_meta id:${pixelMetaDbId || "–"}, pixel:${pixelId || "–"}`)} disabled={!pageViewActivo}
                      style={{ padding: "10px 18px", background: "var(--blue-sub)", color: "var(--blue)", border: "1px solid var(--blue-brd)", cursor: pageViewActivo ? "pointer" : "not-allowed", opacity: pageViewActivo ? 1 : 0.35, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <FiPlay size={11} /> Simular PageView
                    </motion.button>
                    <motion.button whileHover={events.pur.active ? { background: "rgba(109,191,143,0.15)" } : {}} whileTap={events.pur.active ? { scale: 0.97 } : {}}
                      onClick={() => events.pur.active && logEvent("Purchase", `estado_pixel:1, value:${events.pur.value || "29900"} ${events.pur.currency || "ARS"}, pixel_global:${pixelMode === "global"}, pixel:${pixelMode === "global" ? (pixelId || "–") : "cursos.pixel"}`)} disabled={!events.pur.active}
                      style={{ padding: "10px 18px", background: "var(--green-dim)", color: "var(--green)", border: "1px solid var(--green-brd)", cursor: events.pur.active ? "pointer" : "not-allowed", opacity: events.pur.active ? 1 : 0.3, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <FiPlay size={11} /> Simular Purchase
                    </motion.button>
                    {eventLog.length > 0 && (
                      <motion.button whileHover={{ opacity: 0.7 }} whileTap={{ scale: 0.97 }} onClick={() => setEventLog([])} style={{ padding: "10px 14px", background: "transparent", color: "var(--ink-3)", border: "1px solid var(--line)", cursor: "pointer", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 5 }}>
                        <FiX size={11} /> Limpiar
                      </motion.button>
                    )}
                  </div>
                  <div style={{ background: "var(--bg-2)", border: "1px solid var(--line)", padding: 16, minHeight: 90, fontFamily: "'Courier New', monospace", fontSize: 11, color: "var(--ink-2)", lineHeight: 1.9 }}>
                    {eventLog.length === 0 ? (
                      <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>// El log de eventos aparecerá aquí…</span>
                    ) : eventLog.map((e) => (
                      <div key={e.id}>
                        <span style={{ color: "var(--blue)" }}>[{e.time}]</span>{" "}
                        <span style={{ color: "var(--gold)" }}>fbq('track', '{e.name}'</span>
                        {e.extra ? <span style={{ color: "var(--ink-2)" }}>, {"{ "}{e.extra}{" }"}</span> : null}
                        <span style={{ color: "var(--gold)" }}>)</span>
                        {" → "}<span style={{ color: "var(--green)" }}>✓ simulado</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Checklist */}
                <div style={{ background: "var(--bg-3)", border: "1px solid var(--line)", padding: "22px 24px", position: "relative" }}>
                  <CardTopLine />
                  <p style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 16 }}>Checklist de verificación</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {checks.map((c, i) => <CheckItem key={i} label={c.label} ok={c.ok} />)}
                  </div>

                  {events.pur.active && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: 16, padding: "10px 14px", background: "var(--bg-2)", border: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 10 }}>
                      {pixelMode === "global" ? <FiGlobe size={13} style={{ color: "var(--blue)" }} /> : <FiTag size={13} style={{ color: "var(--gold)" }} />}
                      <p style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                        Modo Purchase: <strong style={{ fontWeight: 700, color: pixelMode === "global" ? "var(--blue)" : "var(--gold)" }}>
                          {pixelMode === "global"
                            ? `Pixel global — pixel_global = true (pixel_meta #${pixelMetaDbId || "–"})`
                            : `Individual — pixel_global = false — ${Object.values(pixelMap).filter((v) => v?.trim().length >= 10).length} de ${cursos.length} cursos con pixel`
                          }
                        </strong>
                      </p>
                    </motion.div>
                  )}

                  {/* Mapa de tablas */}
                  <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--bg-2)", border: "1px solid var(--line)" }}>
                    <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 10 }}>Destino de datos en BD</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {[
                        { dot: "var(--blue)",  code: "pixel_meta.pixel_id",     color: "var(--blue)",  val: pixelId ? <span style={{ color: "var(--gold)" }}>{pixelId}</span> : <span style={{ color: "var(--ink-3)" }}>sin configurar</span> },
                        { dot: pageViewActivo ? "var(--green)" : "var(--red)", code: "pixel_meta.estado", color: pageViewActivo ? "var(--green)" : "var(--red)", val: <span style={{ color: pageViewActivo ? "var(--green)" : "var(--red)" }}>{pageViewActivo ? "1 — PageView se dispara en el <head>" : "0 — PageView NO se dispara"}</span> },
                        { dot: pixelMode === "global" ? "var(--blue)" : "var(--gold)", code: "pixel_meta.pixel_global", color: "var(--gold)", val: <span style={{ color: pixelMode === "global" ? "var(--blue)" : "var(--gold)" }}>{pixelMode === "global" ? "true (modo global activo)" : "false (modo individual activo)"}</span> },
                        { dot: events.pur.active ? "var(--green)" : "var(--red)", code: "cursos.estado_pixel", color: events.pur.active ? "var(--green)" : "var(--red)", val: <span style={{ color: events.pur.active ? "var(--green)" : "var(--red)" }}>{events.pur.active ? `1 — Purchase activo (${cursos.filter(c => Number(c.estado_pixel) === 1).length}/${cursos.length} cursos)` : "0 — Purchase desactivado en todos los cursos"}</span> },
                        { dot: "var(--gold)",  code: "cursos.pixel",            color: "var(--gold)",  val: <span style={{ color: "var(--ink-2)" }}>{Object.values(pixelMap).filter(v => v?.trim().length >= 10).length} asignados</span> },
                      ].map(({ dot, code, color, val }, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                          <span style={{ fontSize: 11, fontWeight: 300, color: "var(--ink-2)" }}>
                            <code style={{ fontFamily: "Courier New", color, fontSize: 11 }}>{code}</code>{" → "}{val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* ── TOAST ── */}
        <AnimatePresence>
          {toast.show && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              style={{ position: "fixed", bottom: 24, right: 24, zIndex: 999, background: "var(--bg-3)", border: `1px solid ${toast.type === "err" ? "rgba(224,122,122,0.4)" : toast.type === "warn" ? "var(--gold-brd)" : "var(--green-brd)"}`, color: toast.type === "err" ? "var(--red)" : toast.type === "warn" ? "var(--gold)" : "var(--green)", fontSize: 12, fontWeight: 600, padding: "12px 20px", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 8 }}
            >
              {toast.type === "err" ? <FiAlertCircle size={13} /> : <FiCheck size={13} />}
              {toast.msg}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default AdminMetaPixel;